/**
 * TechSprint Demo scenes — 16 scenes, ~180 seconds total.
 *
 * Each scene exports a small React component that receives a `progress`
 * prop (0→1, where 1 = end of scene). Use it to sequence internal
 * animations so scenes feel alive.
 *
 * All content is deterministic mock. Nomsa · ABC Wealth · James Smith
 * (and the fake "James Smith" impersonator) are the fixed cast.
 */
import { motion } from "framer-motion";
import {
  ShieldCheck, ShieldAlert, Sparkles, Building2, User, MessageSquare,
  Gavel, FileCheck2, Lock, ArrowRight, CheckCircle2, XCircle, HelpCircle,
  Eye, Radar, Layers, Activity, Award, BookOpen, Music2, Phone, TrendingUp,
  ScrollText, Fingerprint,
} from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";
const CREAM = "#F5EFE6";
const RED = "#E11D48";
const GREEN = "#059669";

/* ═════════════════════════════════════════════════════════
   Scene 01 · Cold open
   ═════════════════════════════════════════════════════════ */
function SceneColdOpen() {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-5">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        className="flex items-center gap-4"
      >
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-2xl"
             style={{ background: GOLD }}>
          <span className="font-display font-bold text-5xl" style={{ color: NAVY }}>A</span>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 1 }}
        className="text-center"
      >
        <div className="text-[11px] tracking-[0.4em] uppercase" style={{ color: GOLD }}>
          Altara Consumer Intelligence
        </div>
        <div className="font-display text-6xl font-semibold mt-3">Trust before transaction.</div>
        <div className="text-white/60 text-lg mt-4 max-w-2xl mx-auto">
          A live look at one consumer&apos;s journey — from a real investment
          decision to an averted AI-driven scam — and the intelligence it
          generates for the institution behind her.
        </div>
      </motion.div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 02 · Meet Nomsa
   ═════════════════════════════════════════════════════════ */
function SceneMeetNomsa() {
  return (
    <div className="w-full h-full flex items-center justify-center gap-20">
      <motion.div
        initial={{ opacity: 0, x: -30, rotate: -10 }}
        animate={{ opacity: 1, x: 0, rotate: -6 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        className="relative"
      >
        <NomsaPhoneMockup />
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.5 }}
          className="absolute -top-3 -right-3 px-3 py-1 rounded-full text-[10px] font-semibold tracking-widest shadow-lg"
          style={{ background: GOLD, color: NAVY }}
        >
          Wallet · opened
        </motion.div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="max-w-md"
      >
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>Meet</div>
        <div className="font-display text-7xl font-semibold mt-2">Nomsa</div>
        <ul className="mt-6 space-y-2 text-white/75 text-base">
          <li className="flex items-center gap-3"><span className="w-1 h-1 rounded-full" style={{ background: GOLD }} /> 34 · Durban · KZN</li>
          <li className="flex items-center gap-3"><span className="w-1 h-1 rounded-full" style={{ background: GOLD }} /> Inherited R450 000 · never invested before</li>
          <li className="flex items-center gap-3"><span className="w-1 h-1 rounded-full" style={{ background: GOLD }} /> A client of ABC Wealth · adviser James Smith</li>
        </ul>
        <div className="mt-6 pt-4 border-t border-white/10 text-[13px] text-white/50">
          She opens the Altara wallet on her phone — the single place her
          trust journey lives.
        </div>
      </motion.div>
    </div>
  );
}

/** iPhone-style phone frame with the Altara consumer wallet home screen inside. */
function NomsaPhoneMockup() {
  return (
    <div
      className="relative"
      style={{
        width: 300,
        height: 620,
        borderRadius: 44,
        padding: 12,
        background: "linear-gradient(145deg, #111 0%, #1a1a1a 100%)",
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7), 0 0 0 2px rgba(255,255,255,0.05) inset",
      }}
    >
      {/* Notch */}
      <div
        className="absolute top-3 left-1/2 -translate-x-1/2 rounded-b-2xl z-20"
        style={{ width: 110, height: 22, background: "#0a0a0a" }}
      />
      {/* Screen */}
      <div
        className="relative w-full h-full rounded-[32px] overflow-hidden"
        style={{ background: CREAM, color: NAVY }}
      >
        {/* Status bar */}
        <div className="flex items-center justify-between px-6 pt-4 pb-2 text-[10px] font-semibold" style={{ color: NAVY }}>
          <span>9:41</span>
          <span className="flex items-center gap-1">
            <span>•••</span>
            <span>􀛨</span>
            <span className="ml-1">100%</span>
          </span>
        </div>

        {/* Wallet header */}
        <div className="px-5 pt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: NAVY }}>
              <span className="font-display font-bold text-lg" style={{ color: GOLD }}>A</span>
            </div>
            <div>
              <div className="text-[8px] tracking-[0.25em] uppercase" style={{ color: NAVY, opacity: 0.55 }}>Altara</div>
              <div className="text-[11px] font-semibold leading-tight" style={{ color: NAVY }}>Trust Wallet</div>
            </div>
          </div>
          <div className="w-8 h-8 rounded-full flex items-center justify-center"
               style={{ background: `${NAVY}12`, color: NAVY }}>
            <User className="w-4 h-4" />
          </div>
        </div>

        {/* Greeting */}
        <div className="px-5 mt-4">
          <div className="text-[10px] tracking-[0.2em] uppercase" style={{ color: NAVY, opacity: 0.5 }}>Good morning</div>
          <div className="font-display text-2xl font-semibold" style={{ color: NAVY }}>Nomsa</div>
        </div>

        {/* Trust score card */}
        <div className="mx-5 mt-3 rounded-2xl p-4 shadow-md"
             style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #142958 100%)`, color: "white" }}>
          <div className="flex items-center justify-between">
            <div className="text-[9px] tracking-[0.25em] uppercase" style={{ color: GOLD }}>Trust score</div>
            <div className="text-[9px] tracking-widest" style={{ color: "rgba(255,255,255,0.5)" }}>Live</div>
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <div className="font-display text-4xl font-semibold">A</div>
            <div className="text-[10px] text-white/60">Verified adviser · 1 active decision</div>
          </div>
          <div className="mt-3 h-1 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: "82%", background: GOLD }} />
          </div>
        </div>

        {/* Quick tiles grid */}
        <div className="px-5 mt-4 grid grid-cols-2 gap-2">
          <PhoneTile Icon={Gavel}       label="Decisions"   note="1 new" />
          <PhoneTile Icon={ShieldCheck} label="Institutions" note="ABC Wealth" />
          <PhoneTile Icon={ShieldAlert} label="Trust & Safety" note="Run check" />
          <PhoneTile Icon={FileCheck2}  label="Documents"    note="Empty" muted />
        </div>

        {/* Notification banner */}
        <div className="mx-5 mt-3 rounded-xl p-2.5 flex items-center gap-2 border"
             style={{ background: `${GOLD}15`, borderColor: `${GOLD}55` }}>
          <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
               style={{ background: GOLD }}>
            <Sparkles className="w-3 h-3" style={{ color: NAVY }} />
          </div>
          <div className="text-[10px] leading-tight" style={{ color: NAVY }}>
            <strong>New proposal from ABC Wealth</strong><br />
            <span style={{ opacity: 0.7 }}>Tap to begin your governed journey</span>
          </div>
        </div>

        {/* Bottom nav */}
        <div className="absolute left-0 right-0 bottom-0 px-5 py-3 flex items-center justify-around border-t"
             style={{ background: "white", borderColor: `${NAVY}0F` }}>
          <PhoneNavIcon Icon={ShieldCheck} label="Home" active />
          <PhoneNavIcon Icon={FileCheck2}  label="Docs" />
          <PhoneNavIcon Icon={Sparkles}    label="NAVI" />
          <PhoneNavIcon Icon={Gavel}       label="Decide" />
        </div>
      </div>
    </div>
  );
}

function PhoneTile({ Icon, label, note, muted }) {
  return (
    <div
      className="rounded-xl p-2.5 flex flex-col gap-1 border"
      style={{
        background: muted ? "transparent" : "white",
        borderColor: `${NAVY}12`,
        opacity: muted ? 0.55 : 1,
      }}
    >
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3" style={{ color: NAVY }} />
        <div className="text-[9px] font-semibold tracking-wide" style={{ color: NAVY }}>{label}</div>
      </div>
      <div className="text-[9px]" style={{ color: NAVY, opacity: 0.55 }}>{note}</div>
    </div>
  );
}

function PhoneNavIcon({ Icon, label, active }) {
  return (
    <div className="flex flex-col items-center gap-0.5" style={{ color: active ? NAVY : `${NAVY}66` }}>
      <Icon className="w-4 h-4" />
      <span className="text-[8px] font-medium">{label}</span>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 03 · Two forces
   ═════════════════════════════════════════════════════════ */
function SceneTwoForces() {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-10">
      <div className="text-white/70 text-lg max-w-2xl text-center">
        Every consumer today faces two forces at once.
      </div>
      <div className="grid grid-cols-2 gap-8 w-full max-w-4xl">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="rounded-2xl p-8 border shadow-2xl"
          style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(201,162,74,0.4)" }}
        >
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
               style={{ background: `${GOLD}22` }}>
            <Building2 className="w-6 h-6" style={{ color: GOLD }} />
          </div>
          <div className="text-2xl font-semibold">Real institutions</div>
          <div className="text-white/60 mt-2 text-sm">
            Regulated advisers doing real work — but often invisible to consumers
            until months after the decision.
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="rounded-2xl p-8 border shadow-2xl"
          style={{ background: "rgba(225,29,72,0.08)", borderColor: "rgba(225,29,72,0.4)" }}
        >
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
               style={{ background: "rgba(225,29,72,0.2)" }}>
            <ShieldAlert className="w-6 h-6" style={{ color: RED }} />
          </div>
          <div className="text-2xl font-semibold">AI-driven scams</div>
          <div className="text-white/60 mt-2 text-sm">
            Impersonation attacks that clone advisers, brands and voices —
            targeting the exact moment a consumer is about to act.
          </div>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="text-white/50 text-sm"
      >
        Altara sits between them — making trust visible, and impersonation obvious.
      </motion.div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 04 · Proposal arrives
   ═════════════════════════════════════════════════════════ */
function SceneProposalArrives() {
  return (
    <div className="w-full h-full grid grid-cols-2 gap-12 items-center">
      <div className="space-y-4">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>Act I · The right decision</div>
        <div className="text-4xl font-semibold leading-tight">
          ABC Wealth sends Nomsa a proposal.
        </div>
        <div className="text-white/60 text-base">
          It lands in <strong className="text-white">My Decisions</strong> — a
          governed 4-stage journey, not just an email.
        </div>
        <div className="flex items-center gap-2 text-white/50 text-sm mt-4">
          <Building2 className="w-4 h-4" style={{ color: GOLD }} />
          ABC Wealth · FSP 48291
          <span className="mx-2">·</span>
          <User className="w-4 h-4" style={{ color: GOLD }} />
          Adviser James Smith
        </div>
      </div>
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: "white", color: NAVY }}
      >
        <div className="px-6 py-4 flex items-center justify-between" style={{ background: NAVY, color: "white" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: GOLD }}>
              <Gavel className="w-4 h-4" style={{ color: NAVY }} />
            </div>
            <div>
              <div className="text-[9px] tracking-[0.25em] uppercase" style={{ color: GOLD }}>New decision</div>
              <div className="text-sm font-semibold">Investment proposal · R450 000</div>
            </div>
          </div>
          <div className="text-[10px] tracking-widest px-2 py-0.5 rounded"
               style={{ background: `${GOLD}33`, color: GOLD }}>TRUST_PENDING</div>
        </div>
        <div className="p-6 space-y-3">
          <Row label="From" value="ABC Wealth · James Smith" />
          <Row label="Product" value="Balanced Growth Portfolio · Reg 28" />
          <Row label="Recommended contribution" value="R 450 000 lump sum" />
          <Row label="Evidence anchor" value="atc-4c9a1e2b78d1" mono />
          <div className="pt-2 flex items-center gap-2 text-sm" style={{ color: NAVY }}>
            <ArrowRight className="w-4 h-4" style={{ color: GOLD }} />
            <span className="font-medium">Begin governed journey</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between items-baseline gap-4">
      <span className="text-[10px] tracking-widest uppercase text-slate-500">{label}</span>
      <span className={`text-sm ${mono ? "font-mono" : "font-medium"}`}>{value}</span>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 05 · TRUST stage
   ═════════════════════════════════════════════════════════ */
function SceneTrust() {
  return (
    <StageScreen
      stage="1 · Trust"
      title="Is this really them?"
      copy="Altara verifies ABC Wealth against the regulator and confirms James Smith is a real adviser at that firm — before Nomsa reads a word of the proposal."
      chips={[
        { icon: ShieldCheck, label: "FSCA record matched · FSP 48291", tone: "ok" },
        { icon: ShieldCheck, label: "James Smith · Rep #A-2214-JS · active", tone: "ok" },
        { icon: ShieldCheck, label: "Channel on record · +27 11 555 0110", tone: "ok" },
      ]}
      badge="TRUST_ACKNOWLEDGED"
    />
  );
}

function StageScreen({ stage, title, copy, chips, badge }) {
  return (
    <div className="w-full h-full grid grid-cols-5 gap-10 items-center">
      <div className="col-span-2 space-y-4">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>Stage {stage}</div>
        <div className="text-5xl font-display font-semibold leading-tight">{title}</div>
        <div className="text-white/65 text-base leading-relaxed">{copy}</div>
      </div>
      <div className="col-span-3 rounded-2xl border shadow-2xl overflow-hidden"
           style={{ background: "white", color: NAVY, borderColor: `${GOLD}55` }}>
        <div className="flex items-center justify-between px-5 py-3" style={{ background: NAVY, color: "white" }}>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Gavel className="w-4 h-4" style={{ color: GOLD }} />
            Decision journey
          </div>
          <div className="text-[10px] tracking-widest px-2 py-0.5 rounded"
               style={{ background: `${GOLD}33`, color: GOLD }}>{badge}</div>
        </div>
        <div className="p-6 space-y-3">
          {chips.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + i * 0.25, duration: 0.5 }}
              className="flex items-center gap-3 rounded-xl border p-3"
              style={{ background: c.tone === "ok" ? "#ECFDF5" : "#FEE2E2", borderColor: c.tone === "ok" ? "#6EE7B7" : "#FCA5A5" }}
            >
              <c.icon className="w-5 h-5" style={{ color: c.tone === "ok" ? GREEN : RED }} />
              <div className="text-sm font-medium">{c.label}</div>
              <CheckCircle2 className="w-4 h-4 ml-auto" style={{ color: c.tone === "ok" ? GREEN : RED }} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 06 · UNDERSTAND stage
   ═════════════════════════════════════════════════════════ */
function SceneUnderstand() {
  return (
    <div className="w-full h-full grid grid-cols-5 gap-10 items-center">
      <div className="col-span-2 space-y-4">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>Stage 2 · Understand</div>
        <div className="text-5xl font-display font-semibold leading-tight">NAVI translates the fine print.</div>
        <div className="text-white/65 text-base leading-relaxed">
          Altara&apos;s explainer, NAVI, breaks the proposal into plain
          English. Jargon becomes glossary. Every claim traces to a source.
        </div>
      </div>
      <div className="col-span-3 rounded-2xl border shadow-2xl p-6"
           style={{ background: "white", color: NAVY, borderColor: `${GOLD}55` }}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4" style={{ color: GOLD }} />
          <span className="text-[10px] tracking-[0.25em] uppercase" style={{ color: GOLD }}>NAVI · in plain English</span>
        </div>
        <div className="space-y-3">
          <NaviLine delay={0.2}
            en="Your money is spread across shares, bonds and cash. Reg 28 caps how much can sit in each — a legal seat-belt for retirement savings." />
          <NaviLine delay={0.6}
            en="Expected: 10 – 12% per year over 5 years. Real returns aren't promised — markets go up and down." />
          <NaviLine delay={1.0}
            en="Costs come off first: 1.15% per year. On R450k that's about R5 175 in year 1." />
        </div>
        <div className="mt-5 pt-4 border-t border-slate-200 flex items-center gap-2 text-[11px] text-slate-500">
          <BookOpen className="w-3.5 h-3.5" />
          <span>3 terms auto-linked to Nomsa&apos;s glossary · every claim sources the proposal</span>
        </div>
      </div>
    </div>
  );
}

function NaviLine({ en, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.55 }}
      className="flex items-start gap-3"
    >
      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
           style={{ background: `${GOLD}22`, color: GOLD }}>
        <Sparkles className="w-3 h-3" />
      </div>
      <div className="text-[14px] leading-relaxed text-slate-700">{en}</div>
    </motion.div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 07 · CHECK stage
   ═════════════════════════════════════════════════════════ */
function SceneCheck() {
  const flags = [
    "I understand what I&apos;m being sold",
    "This suits my goals (retirement in 25 years)",
    "I can afford it without touching emergency savings",
  ];
  return (
    <div className="w-full h-full grid grid-cols-5 gap-10 items-center">
      <div className="col-span-2 space-y-4">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>Stage 3 · Check</div>
        <div className="text-5xl font-display font-semibold leading-tight">Three checks. In Nomsa&apos;s own words.</div>
        <div className="text-white/65 text-base leading-relaxed">
          Not a tick-box. Each answer becomes evidence, hash-chained into
          Nomsa&apos;s Consumer Intelligence Profile.
        </div>
      </div>
      <div className="col-span-3 rounded-2xl border shadow-2xl p-6"
           style={{ background: "white", color: NAVY, borderColor: `${GOLD}55` }}>
        <div className="space-y-3">
          {flags.map((label, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.25 + i * 0.4, duration: 0.5 }}
              className="flex items-center gap-3 rounded-xl border p-3.5"
              style={{ background: "#ECFDF5", borderColor: "#6EE7B7" }}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center"
                   style={{ background: GREEN, color: "white" }}>
                <CheckCircle2 className="w-4 h-4" />
              </div>
              <div className="text-sm font-medium" style={{ color: NAVY }}
                dangerouslySetInnerHTML={{ __html: label }} />
              <div className="ml-auto text-[10px] tracking-widest uppercase" style={{ color: GREEN }}>
                Evidence logged
              </div>
            </motion.div>
          ))}
        </div>
        <div className="mt-5 flex items-center gap-2 text-[11px] text-slate-500">
          <Fingerprint className="w-3.5 h-3.5" />
          <span>Hash: 8f2c…3b91 · previous: 4a08…c72e · chain sealed</span>
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 08 · ACT stage
   ═════════════════════════════════════════════════════════ */
function SceneAct() {
  return (
    <div className="w-full h-full grid grid-cols-5 gap-10 items-center">
      <div className="col-span-2 space-y-4">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>Stage 4 · Act</div>
        <div className="text-5xl font-display font-semibold leading-tight">Ready to proceed — <span style={{ color: GOLD }}>not executed</span>.</div>
        <div className="text-white/65 text-base leading-relaxed">
          Altara is the trust layer, not the money mover. Nomsa records her
          intended next step; ABC Wealth executes on their own systems.
          Provenance is preserved.
        </div>
      </div>
      <div className="col-span-3 rounded-2xl border shadow-2xl p-7"
           style={{ background: "white", color: NAVY, borderColor: `${GOLD}55` }}>
        <div className="flex items-center justify-between mb-5">
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500">Consumer intent</div>
          <div className="text-[10px] tracking-widest px-2 py-0.5 rounded"
               style={{ background: `${GREEN}22`, color: GREEN }}>ACT_LOGGED</div>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="rounded-xl border p-5 mb-4"
          style={{ background: `${GOLD}0F`, borderColor: `${GOLD}55` }}
        >
          <div className="text-[10px] tracking-widest uppercase" style={{ color: GOLD }}>Nomsa&apos;s next step</div>
          <div className="text-2xl font-semibold mt-1">Ready to proceed</div>
          <div className="text-sm text-slate-600 mt-2">
            &ldquo;Please contact me next week to sign the paperwork.&rdquo;
          </div>
        </motion.div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <MiniStat label="Certificate" value="ATC-4C9A1E2B78D1" mono />
          <MiniStat label="Signed" value="Ed25519" />
          <MiniStat label="Sealed at" value="09 Feb · 14:22" />
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, mono }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
      <div className="text-[9px] tracking-widest uppercase text-slate-500">{label}</div>
      <div className={`text-[12px] mt-1 ${mono ? "font-mono" : "font-medium"}`} style={{ color: NAVY }}>{value}</div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 09 · TikTok advert
   ═════════════════════════════════════════════════════════ */
function SceneTikTok() {
  return (
    <div className="w-full h-full flex items-center justify-center gap-16">
      <div className="max-w-md">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: RED }}>Act II · The scam attempt</div>
        <div className="text-5xl font-display font-semibold mt-3 leading-tight">Three days later…</div>
        <div className="text-white/70 mt-4 text-base leading-relaxed">
          Nomsa scrolls TikTok. An ad for &ldquo;ABC Wealth&rdquo; plays. The
          voice, the branding, even the adviser&apos;s face — all AI-cloned.
          It looks real enough.
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        className="relative"
      >
        <div className="w-72 h-[500px] rounded-[36px] overflow-hidden border-8 shadow-2xl relative"
             style={{ background: "black", borderColor: "#111" }}>
          <div className="absolute inset-0 flex flex-col">
            <div className="flex-1 flex items-center justify-center relative"
                 style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)" }}>
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="w-32 h-32 rounded-full flex items-center justify-center shadow-2xl"
                style={{ background: `${GOLD}66` }}
              >
                <Building2 className="w-14 h-14" style={{ color: GOLD }} />
              </motion.div>
              <div className="absolute bottom-16 left-4 right-4 text-white">
                <div className="text-xs opacity-70">@abcwealth.official</div>
                <div className="text-base font-semibold mt-1">Turn R10k into R100k · guaranteed</div>
                <div className="text-[10px] opacity-60 mt-1">DM us for exclusive access 💰</div>
              </div>
              <div className="absolute top-4 right-4 flex items-center gap-1 text-white/80">
                <Music2 className="w-4 h-4" />
                <span className="text-[10px]">TikTok</span>
              </div>
            </div>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute -bottom-4 -right-4 px-3 py-1 rounded-full text-[10px] font-semibold tracking-widest"
          style={{ background: RED, color: "white" }}
        >AI-CLONED</motion.div>
      </motion.div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 10 · WhatsApp arrives
   ═════════════════════════════════════════════════════════ */
function SceneWhatsApp() {
  return (
    <div className="w-full h-full flex items-center justify-center gap-16">
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7 }}
        className="w-80 rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: "#e5ddd5" }}
      >
        <div className="px-4 py-3 flex items-center gap-3" style={{ background: "#075e54", color: "white" }}>
          <div className="w-9 h-9 rounded-full bg-slate-300 flex items-center justify-center">
            <User className="w-5 h-5" style={{ color: "#075e54" }} />
          </div>
          <div>
            <div className="text-sm font-semibold">James Smith</div>
            <div className="text-[10px] opacity-70">+27 71 555 0221 · online</div>
          </div>
        </div>
        <div className="p-4 space-y-2 min-h-[280px]">
          <Bubble delay={0.3}>Hi Nomsa! It&apos;s James from ABC Wealth 👋</Bubble>
          <Bubble delay={0.9}>I saw you were interested in our new fund on TikTok. Great news!</Bubble>
          <Bubble delay={1.5}>
            <strong>Urgent</strong> — we&apos;ve moved to a <strong>new bank account</strong>. Please transfer your R450k contribution <strong>today only</strong>.
          </Bubble>
          <Bubble delay={2.1}>FNB · 62890****21 · use ref &ldquo;NOMSA&rdquo;. Send proof once done ✅</Bubble>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5, duration: 0.6 }}
        className="max-w-sm"
      >
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: RED }}>Impersonation attempt</div>
        <div className="text-3xl font-display font-semibold mt-2">Not from a channel on record.</div>
        <div className="text-white/60 mt-3 text-sm space-y-1">
          <div>· Wrong WhatsApp number</div>
          <div>· New payment instructions</div>
          <div>· Urgency pressure</div>
          <div>· Contact triggered from TikTok</div>
        </div>
      </motion.div>
    </div>
  );
}

function Bubble({ children, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35 }}
      className="max-w-[240px] rounded-lg px-3 py-2 text-[13px] shadow-sm"
      style={{ background: "white", color: "#111" }}
    >{children}</motion.div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 11 · Trust Check — evidence submitted
   ═════════════════════════════════════════════════════════ */
function SceneTrustCheckEvidence() {
  return (
    <div className="w-full h-full grid grid-cols-5 gap-10 items-center">
      <div className="col-span-2 space-y-4">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>Trust &amp; Safety</div>
        <div className="text-5xl font-display font-semibold leading-tight">Nomsa runs a Trust Check.</div>
        <div className="text-white/65 text-base leading-relaxed">
          One tap. She drops the message in, tells us where it arrived, and
          who it claims to be from. Altara compares it against her real
          trusted-institution record.
        </div>
      </div>
      <div className="col-span-3 rounded-2xl border shadow-2xl p-6"
           style={{ background: "white", color: NAVY, borderColor: `${GOLD}55` }}>
        <div className="text-[10px] tracking-widest uppercase mb-3" style={{ color: GOLD }}>Evidence bundle</div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-3 text-[13px] font-mono text-slate-700"
        >
          &ldquo;Hi Nomsa — urgent, new bank account, transfer R450k today only.&rdquo;
        </motion.div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <EvidenceChip label="Platform"           value="WhatsApp"     delay={0.4} />
          <EvidenceChip label="Claimed institution" value="ABC Wealth"   delay={0.6} />
          <EvidenceChip label="Claimed person"      value="James Smith"  delay={0.8} />
          <EvidenceChip label="Sender number"       value="+27 71 555 0221" mono delay={1.0} />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.5 }}
          className="rounded-xl border p-3 flex items-center gap-3"
          style={{ background: `${NAVY}0A`, borderColor: `${NAVY}22` }}
        >
          <Radar className="w-5 h-5 animate-pulse" style={{ color: NAVY }} />
          <div className="text-[13px] font-medium">Running Trust Check against 7 official channels on record…</div>
        </motion.div>
      </div>
    </div>
  );
}

function EvidenceChip({ label, value, mono, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="rounded-lg border border-slate-200 bg-white p-2.5"
    >
      <div className="text-[9px] tracking-widest uppercase text-slate-500">{label}</div>
      <div className={`text-[13px] ${mono ? "font-mono" : "font-medium"} text-slate-800`}>{value}</div>
    </motion.div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 12 · Trust Check — verdict (longest scene, 20s)
   ═════════════════════════════════════════════════════════ */
function SceneTrustCheckVerdict({ progress }) {
  return (
    <div className="w-full h-full flex flex-col justify-center max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl p-5 border-2 shadow-2xl mb-4 flex items-center gap-4"
        style={{ background: "#FEE2E2", borderColor: "#FCA5A5", color: "#7f1d1d" }}
      >
        <XCircle className="w-9 h-9 flex-shrink-0" style={{ color: RED }} />
        <div>
          <div className="text-[10px] tracking-widest uppercase" style={{ color: RED }}>High risk · impersonation attempt</div>
          <div className="text-2xl font-semibold">This is not James Smith.</div>
        </div>
        <div className="ml-auto text-[11px] tracking-widest uppercase px-3 py-1 rounded-full"
             style={{ background: RED, color: "white" }}>Blocked</div>
      </motion.div>
      <div className="grid grid-cols-4 gap-3">
        <VerdictCard
          Icon={ShieldCheck} title="Verify these first" tone="green" delay={0.4}
          items={[
            "ABC Wealth is real (FSP 48291)",
            "James Smith is your real adviser",
            "Call +27 11 555 0110 to confirm",
          ]}
        />
        <VerdictCard
          Icon={XCircle} title="Doesn't match" tone="red" delay={0.7}
          items={[
            "Not from official WhatsApp",
            "New payment instructions",
            "TikTok-triggered contact",
          ]}
        />
        <VerdictCard
          Icon={HelpCircle} title="Couldn't verify" tone="slate" delay={1.0}
          items={[
            "No matching Altara certificate",
            "Sender identity unconfirmed",
          ]}
        />
        <VerdictCard
          Icon={Eye} title="Look closer" tone="amber" delay={1.3}
          items={[
            "Urgency language",
            "Guaranteed-return language",
          ]}
        />
      </div>
    </div>
  );
}

function VerdictCard({ Icon, title, tone, items, delay }) {
  const palette = {
    green: { color: GREEN, bg: "#ECFDF5", border: "#6EE7B7" },
    red:   { color: RED, bg: "#FEE2E2", border: "#FCA5A5" },
    slate: { color: "#334155", bg: "#F1F5F9", border: "#CBD5E1" },
    amber: { color: "#B45309", bg: "#FEF3C7", border: "#FCD34D" },
  }[tone];
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className="rounded-xl border p-4 flex flex-col"
      style={{ background: palette.bg, borderColor: palette.border, color: palette.color }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <div className="text-xs font-semibold">{title}</div>
      </div>
      <ul className="space-y-1.5 text-[11px] leading-snug" style={{ color: "#334155" }}>
        {items.map((it, i) => (
          <li key={i} className="pl-2 border-l-2" style={{ borderColor: palette.border }}>{it}</li>
        ))}
      </ul>
    </motion.div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 13 · Consumer Intelligence Profile updated
   ═════════════════════════════════════════════════════════ */
function SceneProfileUpdated() {
  return (
    <div className="w-full h-full grid grid-cols-5 gap-10 items-center">
      <div className="col-span-2 space-y-4">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>Act III · Institution intelligence</div>
        <div className="text-5xl font-display font-semibold leading-tight">One consumer. One profile. Enriched by every event.</div>
        <div className="text-white/65 text-base leading-relaxed">
          Both the decision and the averted scam land in Nomsa&apos;s Consumer
          Intelligence Profile. ABC Wealth sees a living view of her trust
          journey — not a form filled six months ago.
        </div>
      </div>
      <div className="col-span-3 rounded-2xl overflow-hidden shadow-2xl"
           style={{ background: "white", color: NAVY }}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ background: NAVY, color: "white" }}>
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4" style={{ color: GOLD }} />
            <span className="text-sm font-semibold">Consumer Intelligence Profile · Nomsa</span>
          </div>
          <div className="text-[10px] tracking-widest px-2 py-0.5 rounded"
               style={{ background: `${GOLD}33`, color: GOLD }}>LIVE</div>
        </div>
        <div className="p-6 grid grid-cols-3 gap-3">
          <ProfileTile Icon={Gavel}       label="Decisions" value="1" note="Ready to proceed" delay={0.2} />
          <ProfileTile Icon={ShieldAlert} label="Scams intercepted" value="1" note="Impersonation" delay={0.5} tone="red" />
          <ProfileTile Icon={ShieldCheck} label="Trusted institutions" value="1" note="ABC Wealth" delay={0.8} />
          <ProfileTile Icon={BookOpen}    label="Understanding events" value="3" note="Reg 28 · fees · returns" delay={1.1} />
          <ProfileTile Icon={ScrollText}  label="Evidence entries" value="8" note="Hash-chained" delay={1.4} />
          <ProfileTile Icon={Award}       label="Trust score" value="A" note="Verified adviser channel" delay={1.7} tone="gold" />
        </div>
      </div>
    </div>
  );
}

function ProfileTile({ Icon, label, value, note, tone, delay }) {
  const bg = tone === "red" ? "#FEE2E2" : tone === "gold" ? `${GOLD}22` : "#F1F5F9";
  const color = tone === "red" ? RED : tone === "gold" ? GOLD : NAVY;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="rounded-xl p-3.5"
      style={{ background: bg }}
    >
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <div className="text-[9px] tracking-widest uppercase" style={{ color }}>{label}</div>
      </div>
      <div className="text-2xl font-semibold mt-1" style={{ color }}>{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{note}</div>
    </motion.div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 14 · Conduct + TCF outcomes
   ═════════════════════════════════════════════════════════ */
function SceneConduct() {
  return (
    <div className="w-full h-full grid grid-cols-5 gap-10 items-center">
      <div className="col-span-2 space-y-4">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>Conduct signals</div>
        <div className="text-5xl font-display font-semibold leading-tight">Every TCF outcome, evidenced.</div>
        <div className="text-white/65 text-base leading-relaxed">
          Nomsa&apos;s journey pre-populates ABC Wealth&apos;s Treating Customers
          Fairly dashboard. The regulator&apos;s six outcomes aren&apos;t a report
          — they are the by-product of the platform doing its job.
        </div>
      </div>
      <div className="col-span-3 rounded-2xl border shadow-2xl overflow-hidden"
           style={{ background: "white", color: NAVY, borderColor: `${GOLD}55` }}>
        <div className="px-5 py-3 flex items-center justify-between" style={{ background: NAVY, color: "white" }}>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Activity className="w-4 h-4" style={{ color: GOLD }} />
            ABC Wealth · TCF Conduct Board
          </div>
          <div className="text-[10px] tracking-widest text-white/60">Last 24 hours</div>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <TCFRow n="1" name="Culture &amp; strategy" value="Verified"  delay={0.2} />
          <TCFRow n="2" name="Products meet needs"    value="Suitable · Reg 28" delay={0.4} />
          <TCFRow n="3" name="Clear information"      value="NAVI · 3 explanations" delay={0.6} />
          <TCFRow n="4" name="Suitable advice"        value="Adviser verified" delay={0.8} />
          <TCFRow n="5" name="Product performance"    value="Aligned to advice" delay={1.0} />
          <TCFRow n="6" name="Post-sale &amp; complaints" value="1 impersonation blocked" delay={1.2} tone="alert" />
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-2 text-[11px] text-slate-500">
          <TrendingUp className="w-3.5 h-3.5" style={{ color: GREEN }} />
          Conduct score for this cohort · <strong className="text-slate-800">96 / 100</strong> · zero consumer losses.
        </div>
      </div>
    </div>
  );
}

function TCFRow({ n, name, value, tone, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.35 }}
      className="rounded-lg border p-2.5 flex items-center gap-3"
      style={{ background: tone === "alert" ? "#FEF3C7" : "white", borderColor: tone === "alert" ? "#FCD34D" : "#E2E8F0" }}
    >
      <div className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold"
           style={{ background: NAVY, color: GOLD }}>{n}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-widest uppercase text-slate-500" dangerouslySetInnerHTML={{ __html: name }} />
        <div className="text-[12px] font-medium text-slate-800">{value}</div>
      </div>
    </motion.div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 15 · AI oversight · evidence chain
   ═════════════════════════════════════════════════════════ */
function SceneAIOversight() {
  return (
    <div className="w-full h-full grid grid-cols-5 gap-10 items-center">
      <div className="col-span-2 space-y-4">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>AI oversight</div>
        <div className="text-5xl font-display font-semibold leading-tight">Explainable by design.</div>
        <div className="text-white/65 text-base leading-relaxed">
          Every AI decision — every scam flag, every plain-English rewrite —
          traces to observable evidence and named rules. No black-box scores.
          The regulator can walk the chain, one hash at a time.
        </div>
      </div>
      <div className="col-span-3 rounded-2xl border shadow-2xl p-6"
           style={{ background: "white", color: NAVY, borderColor: `${GOLD}55` }}>
        <div className="text-[10px] tracking-widest uppercase mb-3" style={{ color: GOLD }}>Trust Check · rule trace</div>
        <div className="space-y-2">
          <RuleRow rule="channel.whatsapp_not_on_record"  outcome="DOESNT_MATCH" ev="+27 71 555 0221 ≠ +27 82 555 0110" delay={0.15} />
          <RuleRow rule="payment.new_account_via_message" outcome="DOESNT_MATCH" ev="Trigger phrase: 'new bank account'" delay={0.45} />
          <RuleRow rule="pressure.urgency_language"       outcome="LOOK_CLOSER"  ev="Trigger phrase: 'today only'" delay={0.75} />
          <RuleRow rule="cert.altara_signed_document"     outcome="COULDNT_VERIFY" ev="No matching ATC certificate" delay={1.05} />
          <RuleRow rule="anchor.institution_verified"     outcome="VERIFY_FIRST"  ev="ABC Wealth FSP #48291 · active" delay={1.35} />
        </div>
        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
          <div className="flex items-center gap-2">
            <Fingerprint className="w-3.5 h-3.5" />
            Evidence hash: <span className="font-mono text-slate-800">7e91…a204</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-3.5 h-3.5" />
            Signed · Ed25519 · verifiable offline
          </div>
        </div>
      </div>
    </div>
  );
}

function RuleRow({ rule, outcome, ev, delay }) {
  const palette = {
    DOESNT_MATCH:    { color: RED, bg: "#FEE2E2" },
    LOOK_CLOSER:     { color: "#B45309", bg: "#FEF3C7" },
    COULDNT_VERIFY:  { color: "#334155", bg: "#F1F5F9" },
    VERIFY_FIRST:    { color: GREEN, bg: "#ECFDF5" },
  }[outcome];
  return (
    <motion.div
      initial={{ opacity: 0, x: -15 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.35 }}
      className="flex items-center gap-3 rounded-lg border p-2.5"
      style={{ background: "white", borderColor: "#E2E8F0" }}
    >
      <div className="text-[10px] font-mono text-slate-500 w-56 flex-shrink-0">{rule}</div>
      <div className="text-[9px] tracking-widest px-2 py-0.5 rounded-full font-semibold flex-shrink-0"
           style={{ background: palette.bg, color: palette.color }}>{outcome}</div>
      <div className="text-[11px] text-slate-600 flex-1 min-w-0 truncate">{ev}</div>
    </motion.div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene 16 · Close
   ═════════════════════════════════════════════════════════ */
function SceneClose() {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-8 text-center">
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        className="w-24 h-24 rounded-2xl flex items-center justify-center shadow-2xl"
        style={{ background: GOLD }}
      >
        <span className="font-display font-bold text-6xl" style={{ color: NAVY }}>A</span>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.9 }}
      >
        <div className="text-[11px] tracking-[0.4em] uppercase" style={{ color: GOLD }}>Altara Consumer Intelligence</div>
        <div className="font-display text-6xl font-semibold mt-3">One consumer.<br/>One profile.<br/>Continuously enriched.</div>
        <div className="text-white/55 mt-6 text-base">Trust made visible. Impersonation made obvious. Conduct made evidenced.</div>
      </motion.div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════
   Scene registry
   ═════════════════════════════════════════════════════════ */
export const SCENES = [
  // Prologue removed — the deck now opens straight on the institution's proposal.
  { act: "Act I · The right decision", durationMs: 12_000, caption: "A proposal from ABC Wealth arrives — as a governed decision, not an email.", subCaption: "My Decisions · 4-stage TechSprint journey", Component: SceneProposalArrives },
  { act: "Act I · Stage 1 · Trust",       durationMs: 12_000, caption: "Altara verifies the institution and the adviser before Nomsa reads a word.", subCaption: "Regulatory record · adviser register · channel on file", Component: SceneTrust },
  { act: "Act I · Stage 2 · Understand",  durationMs: 12_000, caption: "NAVI translates the proposal into plain English — every claim traceable.", subCaption: "Jargon · returns · fees · glossary auto-linked", Component: SceneUnderstand },
  { act: "Act I · Stage 3 · Check",       durationMs: 12_000, caption: "Three checks in Nomsa's own words become hash-chained evidence.", subCaption: "Understanding · suitability · affordability", Component: SceneCheck },
  { act: "Act I · Stage 4 · Act",         durationMs: 12_000, caption: "Nomsa records her intent — Altara is the trust layer, not the money mover.", subCaption: "Certificate sealed · provenance preserved", Component: SceneAct },
  { act: "Act II · The scam attempt",  durationMs: 10_000, caption: "Three days later, an AI-cloned ABC Wealth advert plays on TikTok.", subCaption: "Voice, brand and adviser face — synthetically generated.", Component: SceneTikTok },
  { act: "Act II · Impersonation",     durationMs: 10_000, caption: "A WhatsApp arrives from 'James Smith' — new account, urgent, today only.", subCaption: "Not from any channel on record.", Component: SceneWhatsApp },
  { act: "Act II · Trust & Safety",    durationMs: 10_000, caption: "Nomsa drops the evidence into a Trust Check.", subCaption: "Message · platform · claimed identity — compared to her real record.", Component: SceneTrustCheckEvidence },
  { act: "Act II · Verdict",           durationMs: 20_000, caption: "Impersonation attempt blocked. Four verdict sections, every line evidenced.", subCaption: "Verify these first · Doesn't match · Couldn't verify · Look closer", Component: SceneTrustCheckVerdict },
  { act: "Act III · Institution intelligence", durationMs: 12_000, caption: "Both the decision and the averted scam enrich Nomsa's Consumer Intelligence Profile.", subCaption: "Single source of truth — updated event-by-event.", Component: SceneProfileUpdated },
  { act: "Act III · Conduct signals",  durationMs: 12_000, caption: "ABC Wealth's TCF conduct dashboard is the by-product — not a report to be built.", subCaption: "All six outcomes evidenced. Zero consumer losses in this cohort.", Component: SceneConduct },
  { act: "Act III · AI oversight",     durationMs: 11_000, caption: "Every AI decision traces to a named rule and observable evidence.", subCaption: "Regulator-walkable · hash-chained · signed offline.", Component: SceneAIOversight },
  { act: "Fin",                        durationMs: 5_000,  caption: "Trust made visible. Impersonation made obvious. Conduct made evidenced.", subCaption: "altara.co · Consumer Intelligence Platform", Component: SceneClose },
];
